import React from 'react';
import { Check, AlertCircle, Loader2, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Notification, NotificationType, useNotifications } from '@/contexts/notification-context';
import { COLOR } from '@/styles/constants';

interface ActionNotificationItemProps {
  notification: Notification;
  onClose: () => void;
}

const getTypeIcon = (type: NotificationType) => {
  switch (type) {
    case 'pending':
      return <Clock className="h-3.5 w-3.5" />;
    case 'processing':
      return (
        <div className="flex gap-0.5">
          <div className="w-1 h-1 bg-current rounded-full animate-loading-dots"></div>
          <div className="w-1 h-1 bg-current rounded-full animate-loading-dots delay-150"></div>
          <div className="w-1 h-1 bg-current rounded-full animate-loading-dots delay-300"></div>
        </div>
      );
    case 'success':
      return <Check className="h-3.5 w-3.5" />;
    case 'failed':
      return <AlertCircle className="h-3.5 w-3.5" />;
    default:
      return null;
  }
};

const getTypeIconColor = (type: NotificationType) => {
  switch (type) {
    case 'pending':
      return 'text-primary';
    case 'processing':
      return 'text-primary';
    case 'success':
      return 'text-success';
    case 'failed':
      return 'text-danger';
    default:
      return 'text-dark-grey';
  }
};

function ActionNotificationItem({ notification, onClose }: ActionNotificationItemProps) {
  const iconColor = getTypeIconColor(notification.type);

  return (
    <div
      className={cn(
        "flex items-center gap-2 p-2 rounded-lg shadow-sm border border-light-grey bg-white",
        "animate-in slide-in-from-bottom-5 fade-in duration-300"
      )}
    >
      <div className={cn("flex-shrink-0", iconColor)}>
        {getTypeIcon(notification.type)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate text-dark-blue">
          {notification.message}
        </p>
        {notification.deviceId && (
          <p className="text-[10px] text-dark-grey truncate">
            {notification.deviceId} {notification.datapoint ? `• ${notification.datapoint}` : ''}
          </p>
        )}
      </div>
      <button
        onClick={onClose}
        className="text-dark-grey hover:text-black-blue p-0.5 rounded-full hover:bg-light-grey"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 6 6 18" /><path d="m6 6 12 12" />
        </svg>
      </button>
    </div>
  );
}

export function ActionNotifications() {
  const { notifications, removeNotification } = useNotifications();

  if (notifications.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 flex flex-col gap-2 z-50 max-w-[300px]">
      {notifications.map((notification) => (
        <ActionNotificationItem
          key={notification.id}
          notification={notification}
          onClose={() => removeNotification(notification.id)}
        />
      ))}
    </div>
  );
}
